import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Sparkle,
  Check,
  X,
  Prohibit,
  PencilSimple,
  Brain,
} from "@phosphor-icons/react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "./ui/sheet";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import * as API from "../lib/api";
import { toast } from "sonner";

const CATEGORY_COLORS = {
  identity: "from-fuchsia-500/20 to-fuchsia-500/5 border-fuchsia-500/30 text-fuchsia-300",
  family: "from-rose-500/20 to-rose-500/5 border-rose-500/30 text-rose-300",
  friends: "from-pink-500/20 to-pink-500/5 border-pink-500/30 text-pink-300",
  tasks: "from-amber-500/20 to-amber-500/5 border-amber-500/30 text-amber-300",
  preferences: "from-cyan-500/20 to-cyan-500/5 border-cyan-500/30 text-cyan-300",
  finances: "from-emerald-500/20 to-emerald-500/5 border-emerald-500/30 text-emerald-300",
  goals: "from-violet-500/20 to-violet-500/5 border-violet-500/30 text-violet-300",
  business: "from-indigo-500/20 to-indigo-500/5 border-indigo-500/30 text-indigo-300",
  important_dates:
    "from-orange-500/20 to-orange-500/5 border-orange-500/30 text-orange-300",
  health: "from-teal-500/20 to-teal-500/5 border-teal-500/30 text-teal-300",
  routines: "from-sky-500/20 to-sky-500/5 border-sky-500/30 text-sky-300",
  default: "from-white/10 to-white/0 border-white/15 text-white/70",
};

function CategoryChip({ category }) {
  if (!category) return null;
  const c = CATEGORY_COLORS[category] || CATEGORY_COLORS.default;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gradient-to-br border text-[10px] uppercase tracking-wider ${c}`}
      data-testid={`suggestion-category-${category}`}
    >
      <Sparkle size={9} weight="fill" />
      {category.replace("_", " ")}
    </span>
  );
}

function ConfidenceBar({ value }) {
  const pct = Math.round(Math.max(0, Math.min(1, value || 0)) * 100);
  return (
    <div className="flex items-center gap-2">
      <div className="h-1 bg-white/5 rounded-full flex-1 overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-cyan-500/70 to-cyan-300"
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="text-[10px] text-white/40 tabular-nums">{pct}%</span>
    </div>
  );
}

function SuggestionCard({ s, onAccept, onDismiss, onIgnore, busy }) {
  const [editing, setEditing] = useState(false);
  const [content, setContent] = useState(s.content);

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: -16 }}
      className="rounded-2xl bg-[#0f0f0f] border border-white/5 p-4"
      data-testid={`suggestion-card-${s.id}`}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <CategoryChip category={s.category} />
        <span className="text-[10px] text-white/35">just now</span>
      </div>

      {editing ? (
        <Textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          className="bg-transparent border-white/10 focus-visible:ring-cyan-500/40 min-h-[70px] mb-2"
          data-testid={`suggestion-edit-${s.id}`}
        />
      ) : (
        <p className="text-[15px] text-white/90 leading-relaxed mb-2">{s.content}</p>
      )}

      {s.reasoning && !editing && (
        <p className="text-xs text-white/45 italic mb-2">"{s.reasoning}"</p>
      )}

      <div className="mb-3">
        <ConfidenceBar value={s.confidence} />
      </div>

      <div className="flex items-center gap-1.5 flex-wrap">
        <Button
          size="sm"
          onClick={() => onAccept(s, editing ? content : undefined)}
          disabled={busy || (editing && !content.trim())}
          className="bg-cyan-500 hover:bg-cyan-400 text-black font-medium rounded-full h-8 px-3"
          data-testid={`suggestion-accept-${s.id}`}
        >
          <Check size={12} weight="bold" className="mr-1" />
          {editing ? "Save" : "Accept"}
        </Button>
        {!editing && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => setEditing(true)}
            disabled={busy}
            className="border-white/10 hover:bg-white/5 rounded-full h-8 px-3"
            data-testid={`suggestion-edit-button-${s.id}`}
          >
            <PencilSimple size={12} weight="bold" className="mr-1" />
            Edit
          </Button>
        )}
        <Button
          size="sm"
          variant="ghost"
          onClick={() => onDismiss(s)}
          disabled={busy}
          className="text-white/60 hover:text-white rounded-full h-8 px-3"
          data-testid={`suggestion-dismiss-${s.id}`}
        >
          <X size={12} weight="bold" className="mr-1" />
          Dismiss
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => onIgnore(s)}
          disabled={busy}
          className="text-white/40 hover:text-white/80 rounded-full h-8 px-3"
          data-testid={`suggestion-ignore-${s.id}`}
        >
          <Prohibit size={12} weight="bold" className="mr-1" />
          Ignore similar
        </Button>
      </div>
    </motion.div>
  );
}

export default function SuggestionsSheet({ open, onOpenChange, suggestions, onChange }) {
  const [busy, setBusy] = useState(false);

  const handleAccept = async (s, editedContent) => {
    setBusy(true);
    try {
      const payload = {};
      if (editedContent) payload.content = editedContent;
      await API.acceptSuggestion(s.id, payload);
      toast.success("Saved to memory");
      onChange?.((prev) => prev.filter((x) => x.id !== s.id));
    } catch {
      toast.error("Failed to accept");
    } finally {
      setBusy(false);
    }
  };

  const handleDismiss = async (s) => {
    setBusy(true);
    try {
      await API.dismissSuggestion(s.id);
      onChange?.((prev) => prev.filter((x) => x.id !== s.id));
    } catch {
      toast.error("Failed to dismiss");
    } finally {
      setBusy(false);
    }
  };

  const handleIgnore = async (s) => {
    setBusy(true);
    try {
      await API.ignoreSuggestion(s.id);
      toast("Will not suggest similar memories again");
      onChange?.((prev) => prev.filter((x) => x.id !== s.id));
    } catch {
      toast.error("Failed to ignore");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        className="bg-[#080808] border-white/10 text-white rounded-t-3xl max-h-[85vh] overflow-y-auto"
        data-testid="suggestions-sheet"
      >
        <SheetHeader className="text-left mb-4">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center">
              <Brain size={18} weight="duotone" className="text-cyan-400" />
            </div>
            <div>
              <SheetTitle className="font-display tracking-tight text-white">
                Suggested memories
              </SheetTitle>
              <SheetDescription className="text-white/55 text-xs">
                Review before saving — your approval is required.
              </SheetDescription>
            </div>
          </div>
        </SheetHeader>

        {suggestions.length === 0 ? (
          <div className="text-center py-10 text-white/40 text-sm">
            No pending suggestions.
          </div>
        ) : (
          <div className="space-y-2.5 pb-4">
            <AnimatePresence>
              {suggestions.map((s) => (
                <SuggestionCard
                  key={s.id}
                  s={s}
                  onAccept={handleAccept}
                  onDismiss={handleDismiss}
                  onIgnore={handleIgnore}
                  busy={busy}
                />
              ))}
            </AnimatePresence>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
